/**
 * Single source of truth for the site's content model.
 *
 * Imported by BOTH the Express backend (validation, seeding, CRUD routing) and
 * the React frontend (admin list columns, editor field rendering, i18n). If a
 * field is not described here it does not exist — add it here first.
 *
 * 站点内容模型的唯一真源。后端（校验、种子数据、CRUD 路由）和前端（后台列表列、
 * 编辑器字段渲染、国际化）都从这里读取。字段没写在这里就等于不存在——先改这里。
 */

// ---------------------------------------------------------------------------
// Roles & permissions / 角色与权限
// ---------------------------------------------------------------------------

/**
 * The three roles. Two of them are what an account can be; the third is not.
 *
 * `admin` and `editor` are held by people and stored on a user row. `agent` is
 * held by nobody: it is the role a request acquires by presenting an API key,
 * decided per request in `effectiveRole` (server/auth.js) and never written to
 * an account — server/routes/users.js refuses to assign it. That is what keeps
 * the key's owner from lending it their own powers: an admin's key is resolved
 * to the admin for attribution and to `agent` for capability, so it drafts and
 * cannot publish like every other key.
 *
 * 三个角色。其中两个是账号可以成为的东西，第三个不是。
 *
 * admin 和 editor 由人持有，存在用户记录上。agent 不属于任何人：它是请求「出示
 * API 密钥」而获得的角色，由 server/auth.js 的 `effectiveRole` 逐个请求裁定，
 * 永远不会写进账号——server/routes/users.js 拒绝分配它。正是这一点让密钥的主人
 * 无法把自己的权力借给它：管理员的密钥，署名解析到管理员，权限解析到 agent，
 * 于是它和其它任何密钥一样只能起草，不能发布。
 *
 * @type {{ADMIN: 'admin', EDITOR: 'editor', AGENT: 'agent'}}
 */
export const ROLES = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  AGENT: 'agent',
}

/**
 * What each role may do.
 *
 * `content` covers every collection below; `users` is admin-only account
 * management; `site` is the global settings blob (group name, contact, about);
 * `stats` is the visit breakdown; `keys` is the API keys a person mints for
 * their own automated callers.
 *
 * 每个角色的能力。content 覆盖下面所有集合；users 是仅管理员可用的账号管理；
 * site 是全局设置（组名、联系方式、简介）；stats 是访问统计明细；
 * keys 是每个人为自己的自动化调用方创建的 API 密钥。
 */
export const PERMISSIONS = {
  [ROLES.ADMIN]: {
    content: ['read', 'create', 'update', 'delete', 'publish'],
    // `readAll` and `revokeAny` make an admin's view of keys different in kind
    // rather than merely in size: anyone may see and revoke their own, an admin
    // may see and revoke anybody's. Without them a leaked key could only be
    // killed by whoever happened to mint it — including after they have left.
    // readAll 与 revokeAny 让管理员看密钥的方式在「性质」上不同，而不只是「数量」上：
    // 谁都能查看和吊销自己的，管理员则能查看和吊销任何人的。没有这两项，一把泄漏的
    // 密钥就只有当初创建它的那个人能销毁——哪怕那个人已经离开了。
    keys: ['read', 'readAll', 'create', 'revoke', 'revokeAny'],
    media: ['read', 'upload', 'delete'],
    site: ['read', 'update'],
    stats: ['read'],
    users: ['read', 'create', 'update', 'delete', 'resetPassword'],
  },
  [ROLES.EDITOR]: {
    content: ['read', 'create', 'update', 'delete', 'publish'],
    // Self-service, with no admin in the loop: an editor mints keys for their own
    // automated helpers and revokes them again. Their own, and only their own —
    // there is no `readAll` here, so one editor cannot even enumerate another's.
    // 完全自助，不需要管理员经手：编辑为自己的自动化助手创建密钥，也自己吊销。
    // 只限自己的——这里没有 readAll，因此一个编辑连另一个编辑有哪些密钥都列不出来。
    keys: ['read', 'create', 'revoke'],
    media: ['read', 'upload', 'delete'],
    site: ['read'],
    // An editor publishes pages; where the readers are is not their business.
    // 编辑负责发布页面；读者来自哪里不属于他的职责范围。
    stats: [],
    users: [],
  },
  // The role an AI writes under, and the one nobody signs in as: it arrives with
  // an API key rather than with an account. It drafts; a human decides what the
  // public sees. Every absence below is deliberate and load-bearing: no `publish`
  // means it cannot put anything in front of a reader and — because the server
  // also gates edits on the STORED status — cannot touch a page that is already
  // live; no `delete` in either area means nothing it can reach is destructible.
  // What is missing from this table is the entire safety story, so do not add an
  // action here to make a screen work.
  // AI 写作时使用的角色，也是没有人能登录成为的那个角色：它随 API 密钥而来，
  // 而不是随账号而来。它只写草稿，公众看到什么由人来决定。下面每一处「缺失」
  // 都是刻意的，且是承重的：没有 publish，它就无法把任何东西推到读者面前，并且
  // ——因为服务端同时按「库里存的状态」拦截修改——它也碰不了已经上线的页面；两个
  // 域都没有 delete，意味着它够得着的东西没有一样是可销毁的。这张表里缺的东西
  // 就是安全性本身，所以不要为了让某个界面能跑而往这里加动作。
  [ROLES.AGENT]: {
    content: ['read', 'create', 'update'],
    // Empty, and load-bearing. This is the row an API key acts under, whoever
    // owns it, so a key that could mint a key would be a key that can escalate:
    // it would issue itself a replacement and outlive the revocation of the one
    // that was noticed. Minting stays with the person, at a browser, signed in.
    // 空的，而且是承重的。这一行正是 API 密钥（不论属于谁）行事时所用的权限，
    // 因此「能创建密钥的密钥」等于「能自我提权的密钥」：它会给自己再签发一把，
    // 从而在被发现的那把被吊销之后继续存活。创建密钥这件事只属于登录着的人。
    keys: [],
    media: ['read', 'upload'],
    site: [],
    stats: [],
    users: [],
  },
}

/**
 * Check a capability without importing the table everywhere.
 *
 * 不必到处引表就能判断权限。
 *
 * @param {string} role Role name / 角色名。
 * @param {string} area One of content|media|site|stats|users|keys / 权限域。
 * @param {string} action e.g. 'update' / 动作。
 * @returns {boolean} True when allowed / 允许则为 true。
 */
export function can(role, area, action) {
  const table = PERMISSIONS[role]
  if (!table) return false
  return (table[area] || []).includes(action)
}

// ---------------------------------------------------------------------------
// Block editor / 块编辑器
// ---------------------------------------------------------------------------

/**
 * Block types the editor can produce and the public site can render.
 *
 * A block-based body (instead of free rich text) is what keeps every page on
 * the same visual system: an editor picks a block, the site decides how it
 * looks. `fields` drives the editor form; `renderer` is the component key.
 *
 * 编辑器可产出、公开页可渲染的块类型。用块而不是自由富文本，是为了让所有页面
 * 保持同一套视觉系统：编辑只选块，样式由站点决定。fields 驱动编辑器表单，
 * renderer 是组件键名。
 */
export const BLOCK_TYPES = {
  heading: {
    labelZh: '小标题',
    labelEn: 'Heading',
    icon: 'H',
    fields: [
      { key: 'text', type: 'text', labelZh: '标题文字', labelEn: 'Heading text', i18n: true },
      {
        key: 'level',
        type: 'select',
        labelZh: '层级',
        labelEn: 'Level',
        options: [
          { value: 2, labelZh: '一级（H2）', labelEn: 'Level 1 (H2)' },
          { value: 3, labelZh: '二级（H3）', labelEn: 'Level 2 (H3)' },
        ],
        default: 2,
      },
    ],
  },
  paragraph: {
    labelZh: '正文段落',
    labelEn: 'Paragraph',
    icon: '¶',
    fields: [
      {
        key: 'text',
        type: 'richtext',
        labelZh: '正文',
        labelEn: 'Body text',
        i18n: true,
        hintZh: '支持 **粗体**、*斜体*、`代码`、[链接](https://…)',
        hintEn: 'Supports **bold**, *italic*, `code`, [link](https://…)',
      },
    ],
  },
  list: {
    labelZh: '列表',
    labelEn: 'List',
    icon: '•',
    fields: [
      {
        key: 'ordered',
        type: 'boolean',
        labelZh: '有序列表',
        labelEn: 'Ordered list',
        default: false,
      },
      {
        key: 'items',
        type: 'textlines',
        labelZh: '条目（每行一条）',
        labelEn: 'Items (one per line)',
        i18n: true,
      },
    ],
  },
  image: {
    labelZh: '图片',
    labelEn: 'Image',
    icon: '▣',
    fields: [
      { key: 'src', type: 'upload', accept: 'image', labelZh: '图片', labelEn: 'Image' },
      { key: 'caption', type: 'text', labelZh: '图注', labelEn: 'Caption', i18n: true },
      {
        key: 'width',
        type: 'select',
        labelZh: '宽度',
        labelEn: 'Width',
        options: [
          { value: 'text', labelZh: '与正文同宽', labelEn: 'Text width' },
          { value: 'wide', labelZh: '加宽', labelEn: 'Wide' },
          { value: 'full', labelZh: '通栏', labelEn: 'Full bleed' },
        ],
        default: 'text',
      },
    ],
  },
  gallery: {
    labelZh: '图片组',
    labelEn: 'Gallery',
    icon: '▦',
    fields: [
      { key: 'items', type: 'uploadList', accept: 'image', labelZh: '图片', labelEn: 'Images' },
      { key: 'caption', type: 'text', labelZh: '图注', labelEn: 'Caption', i18n: true },
      {
        key: 'columns',
        type: 'select',
        labelZh: '每行数量',
        labelEn: 'Columns',
        options: [
          { value: 2, labelZh: '2 张', labelEn: '2' },
          { value: 3, labelZh: '3 张', labelEn: '3' },
          { value: 4, labelZh: '4 张', labelEn: '4' },
        ],
        default: 3,
      },
    ],
  },
  video: {
    labelZh: '视频',
    labelEn: 'Video',
    icon: '▶',
    fields: [
      {
        key: 'src',
        type: 'upload',
        accept: 'video',
        labelZh: '视频文件',
        labelEn: 'Video file',
        hintZh: '也可以留空，改填下面的外链',
        hintEn: 'Leave empty to use an external URL instead',
      },
      {
        key: 'url',
        type: 'text',
        labelZh: '外部视频链接',
        labelEn: 'External video URL',
        hintZh: '直接粘 B 站或 YouTube 的观看页链接即可，会自动转成可播放的地址',
        hintEn: 'Paste the normal Bilibili or YouTube watch link; it is converted automatically',
      },
      { key: 'poster', type: 'upload', accept: 'image', labelZh: '封面图', labelEn: 'Poster' },
      { key: 'caption', type: 'text', labelZh: '说明', labelEn: 'Caption', i18n: true },
    ],
  },
  audio: {
    labelZh: '音频',
    labelEn: 'Audio',
    icon: '♪',
    fields: [
      { key: 'src', type: 'upload', accept: 'audio', labelZh: '音频文件', labelEn: 'Audio file' },
      { key: 'title', type: 'text', labelZh: '标题', labelEn: 'Title', i18n: true },
      { key: 'caption', type: 'text', labelZh: '说明', labelEn: 'Caption', i18n: true },
    ],
  },
  attachment: {
    labelZh: '附件下载',
    labelEn: 'Attachment',
    icon: '⇩',
    fields: [
      { key: 'src', type: 'upload', accept: 'doc', labelZh: '文件', labelEn: 'File' },
      { key: 'label', type: 'text', labelZh: '显示名称', labelEn: 'Display name', i18n: true },
    ],
  },
  quote: {
    labelZh: '引用',
    labelEn: 'Quote',
    icon: '❝',
    fields: [
      { key: 'text', type: 'richtext', labelZh: '引文', labelEn: 'Quote', i18n: true },
      { key: 'cite', type: 'text', labelZh: '出处', labelEn: 'Attribution', i18n: true },
    ],
  },
  code: {
    labelZh: '代码',
    labelEn: 'Code',
    icon: '</>',
    fields: [
      { key: 'language', type: 'text', labelZh: '语言', labelEn: 'Language', default: 'python' },
      { key: 'text', type: 'textarea', labelZh: '代码', labelEn: 'Code' },
    ],
  },
  divider: {
    labelZh: '分隔线',
    labelEn: 'Divider',
    icon: '—',
    fields: [],
  },
}

/**
 * Preset templates: a named starting sequence of blocks.
 *
 * Editors start from one of these instead of a blank page, which is how the
 * site keeps a consistent shape across authors.
 *
 * 预设模板：一组命名好的起始块序列。编辑从模板开始而不是空白页，
 * 这样不同作者写出来的页面结构才一致。
 */
export const TEMPLATES = {
  news_plain: {
    labelZh: '新闻 · 纯文字',
    labelEn: 'News · Text only',
    collection: 'news',
    descriptionZh: '一段导语加两段正文，适合简讯。',
    descriptionEn: 'A lede plus two body paragraphs — good for short notices.',
    blocks: [
      { type: 'paragraph', data: { text: { zh: '（导语：一句话说清这条新闻是什么）', en: '(Lede: one sentence on what happened)' } } },
      { type: 'paragraph', data: { text: { zh: '（正文）', en: '(Body)' } } },
    ],
  },
  news_photo: {
    labelZh: '新闻 · 图文',
    labelEn: 'News · With photos',
    collection: 'news',
    descriptionZh: '导语 + 通栏图 + 正文 + 图片组，适合活动报道。',
    descriptionEn: 'Lede, full-bleed photo, body, gallery — good for event coverage.',
    blocks: [
      { type: 'paragraph', data: { text: { zh: '（导语）', en: '(Lede)' } } },
      { type: 'image', data: { src: '', width: 'wide', caption: { zh: '', en: '' } } },
      { type: 'paragraph', data: { text: { zh: '（正文）', en: '(Body)' } } },
      { type: 'gallery', data: { items: [], columns: 3, caption: { zh: '', en: '' } } },
    ],
  },
  news_award: {
    labelZh: '新闻 · 获奖喜报',
    labelEn: 'News · Award announcement',
    collection: 'news',
    descriptionZh: '获奖信息 + 证书图 + 团队介绍。',
    descriptionEn: 'Award details, certificate image, team credits.',
    blocks: [
      { type: 'paragraph', data: { text: { zh: '（获奖信息：比赛名称、奖项等级、获奖人）', en: '(Award: competition, level, recipients)' } } },
      { type: 'image', data: { src: '', width: 'text', caption: { zh: '获奖证书', en: 'Certificate' } } },
      { type: 'heading', data: { level: 2, text: { zh: '团队成员', en: 'Team' } } },
      { type: 'list', data: { ordered: false, items: { zh: '', en: '' } } },
    ],
  },
  research_project: {
    labelZh: '研究项目 · 标准结构',
    labelEn: 'Project · Standard',
    collection: 'research',
    descriptionZh: '问题背景 + 技术路线 + 成果图 + 相关论文。',
    descriptionEn: 'Problem, approach, result figure, related papers.',
    blocks: [
      { type: 'heading', data: { level: 2, text: { zh: '研究背景', en: 'Background' } } },
      { type: 'paragraph', data: { text: { zh: '（要解决什么问题，为什么难）', en: '(What problem, why it is hard)' } } },
      { type: 'heading', data: { level: 2, text: { zh: '技术路线', en: 'Approach' } } },
      { type: 'paragraph', data: { text: { zh: '（方法思路）', en: '(Method)' } } },
      { type: 'image', data: { src: '', width: 'wide', caption: { zh: '方法框架图', en: 'Framework' } } },
      { type: 'heading', data: { level: 2, text: { zh: '相关成果', en: 'Outcomes' } } },
      { type: 'list', data: { ordered: false, items: { zh: '', en: '' } } },
    ],
  },

  demo_video: {
    labelZh: '演示样例 · 视频演示',
    labelEn: 'Demo · Video walkthrough',
    collection: 'demos',
    descriptionZh: '一段说明 + 演示视频 + 运行环境说明 + 代码链接。',
    descriptionEn: 'A note, the recording, what it runs on, and where the code is.',
    blocks: [
      { type: 'paragraph', data: { text: { zh: '（这段演示在做什么、输入是什么、该看哪里）', en: '(What the demo does, what goes in, where to look)' } } },
      { type: 'video', data: { src: '', url: '', poster: '', caption: { zh: '', en: '' } } },
      { type: 'heading', data: { level: 3, text: { zh: '运行环境', en: 'Runs on' } } },
      { type: 'list', data: { ordered: false, items: { zh: '', en: '' } } },
      { type: 'attachment', data: { src: '', label: { zh: '代码 / 可执行文件', en: 'Code / binary' } } },
    ],
  },

  demo_results: {
    labelZh: '演示样例 · 运行结果',
    labelEn: 'Demo · Results',
    collection: 'demos',
    descriptionZh: '结果图组 + 定量指标 + 复现步骤。适合没有视频的情况。',
    descriptionEn: 'A result grid, the numbers, and how to reproduce. For when there is no video.',
    blocks: [
      { type: 'paragraph', data: { text: { zh: '（在什么数据上跑的，对比对象是谁）', en: '(Which data, compared against what)' } } },
      { type: 'gallery', data: { items: [], columns: 3, caption: { zh: '定性结果', en: 'Qualitative results' } } },
      { type: 'heading', data: { level: 3, text: { zh: '定量指标', en: 'Numbers' } } },
      { type: 'paragraph', data: { text: { zh: '', en: '' } } },
      { type: 'heading', data: { level: 3, text: { zh: '复现', en: 'Reproducing' } } },
      { type: 'code', data: { language: 'bash', text: '' } },
    ],
  },
  member_profile: {
    labelZh: '成员 · 个人简介',
    labelEn: 'Member · Profile',
    collection: 'members',
    descriptionZh: '研究兴趣 + 教育经历 + 代表成果。',
    descriptionEn: 'Interests, education, selected work.',
    blocks: [
      { type: 'heading', data: { level: 2, text: { zh: '研究兴趣', en: 'Research interests' } } },
      { type: 'list', data: { ordered: false, items: { zh: '', en: '' } } },
      { type: 'heading', data: { level: 2, text: { zh: '教育经历', en: 'Education' } } },
      { type: 'list', data: { ordered: false, items: { zh: '', en: '' } } },
    ],
  },
  blank: {
    labelZh: '空白',
    labelEn: 'Blank',
    collection: '*',
    descriptionZh: '从零开始。',
    descriptionEn: 'Start from nothing.',
    blocks: [],
  },
}

// ---------------------------------------------------------------------------
// Field helpers / 字段辅助
// ---------------------------------------------------------------------------

/** Localised string pair used everywhere content is bilingual. 双语字符串对。 */
export const emptyI18n = () => ({ zh: '', en: '' })

/**
 * Upload buckets. Each maps to a subdirectory under server/uploads and a set of
 * accepted mime prefixes.
 *
 * 上传分桶。每个对应 server/uploads 下的一个子目录和一组允许的 mime 前缀。
 */
export const UPLOAD_KINDS = {
  image: {
    dir: 'images',
    mime: ['image/'],
    maxMB: 20,
    // Bytes stay in the database *and* the file is served from object storage.
    // The two are not in tension: the database copy is what keeps "one mysqldump
    // is the whole site" true, and object storage is what stops every picture
    // leaving through a 5 Mbps pipe. 218 MB of images is nothing to MySQL, so
    // there is no reason to give the backup up for the speed.
    // 字节留在数据库里，*同时*文件由对象存储分发。两者并不冲突：数据库里的那份让
    // 「一份 mysqldump 即整个站点」继续成立，而对象存储让每一张图片不必再从一条
    // 5 Mbps 的管子里挤出去。218 MB 的图片对 MySQL 不算什么，所以没有理由为了速度
    // 放弃备份。
    inDatabase: true,
    objectStore: true,
    labelZh: '图片',
    labelEn: 'Image',
  },
  video: {
    dir: 'videos',
    mime: ['video/'],
    // A gigabyte, and it is now a number the whole stack can honour. It could
    // not be before: a single POST of that size dies at nginx's body cap and
    // again at the proxy's five-minute timeout, so the limit would have been a
    // promise the server could not keep. Uploads past 32 MB are sent as 8 MB
    // slices, and every one of those fits comfortably inside both.
    // 一个 GB，而且现在这是整条链路都兑现得了的数字。以前不是：这个尺寸的单次 POST
    // 会先死在 nginx 的请求体上限，再死在代理的五分钟超时上，所以那个上限只是一句
    // 服务端做不到的承诺。超过 32 MB 的上传按 8 MB 分片发送，而每一片都从容地落在这两道限制之内。
    maxMB: 1024,
    // The one bucket whose bytes stay out of the database, and the only one
    // where that is not a choice. MySQL's `max_allowed_packet` is 64 MiB on
    // this server and cannot exceed 1 GiB anywhere, so a feature-length video
    // has no representation as a single BLOB; and the box has 3.6 GB of RAM
    // total, which one buffered 300 MB video would already put under pressure.
    // Video therefore lives on disk (and, once configured, in object storage),
    // and the media row records where — not what.
    //
    // 唯一一个字节不进数据库的分桶，也是唯一一个「不进」并非取舍的分桶。
    // 本服务器的 MySQL `max_allowed_packet` 是 64 MiB，而该参数在任何地方都不能超过
    // 1 GiB，因此一个完整长度的视频根本无法表示为单个 BLOB；何况整台机器只有 3.6 GB 内存，
    // 缓冲一个 300 MB 的视频就已经吃紧。所以视频存在磁盘上（配置好之后还存在对象存储里），
    // 媒体记录里写的是它在哪儿，而不是它是什么。
    inDatabase: false,
    // And into object storage when one is configured. The two flags are not the
    // same question: `inDatabase` is about what MySQL can hold, this is about
    // which machine's bandwidth the bytes leave from. A deployment with no
    // bucket still works — the file simply stays on disk and is served, slowly,
    // from here.
    // 并且在配置了对象存储时存进去。这两个开关问的不是同一件事：`inDatabase` 关心
    // MySQL 装不装得下，这一个关心字节从哪台机器的带宽发出去。没有桶的部署照样能跑
    // ——文件就留在磁盘上，由本机慢慢发。
    objectStore: true,
    labelZh: '视频',
    labelEn: 'Video',
  },
  audio: {
    dir: 'audio',
    mime: ['audio/'],
    maxMB: 100,
    inDatabase: true,
    labelZh: '音频',
    labelEn: 'Audio',
  },
  doc: {
    dir: 'docs',
    mime: ['application/pdf', 'application/', 'text/'],
    // 64, not 100, because 100 was never a number this stack could honour: the
    // bytes are kept in MySQL, and `max_allowed_packet` is 64 MiB on this server
    // and cannot exceed 1 GiB anywhere. A 70 MB PDF would have been accepted by
    // the route and then failed at the insert. The largest document here is
    // 4 MB, so the real limit moves nowhere near anything in use.
    // 64 而不是 100，因为 100 从来不是这条链路兑现得了的数字：字节要留在 MySQL 里，
    // 而本服务器的 `max_allowed_packet` 是 64 MiB，且该参数在任何地方都不能超过 1 GiB。
    // 一个 70 MB 的 PDF 会被路由接受，然后在插入时失败。这里最大的文档是 4 MB，
    // 因此真正的上限离任何在用的东西都还很远。
    maxMB: 64,
    inDatabase: true,
    // A PDF is something people download, and downloading it from here costs the
    // whole site's bandwidth for as long as it takes.
    // PDF 是拿来下载的，而从这里下载它，会在整个下载期间占掉整个站点的带宽。
    objectStore: true,
    labelZh: '文档',
    labelEn: 'Document',
  },
}

// ---------------------------------------------------------------------------
// Collections / 内容集合
// ---------------------------------------------------------------------------

/**
 * Every editable collection.
 *
 * `fields` drives both the admin form and server-side validation. `listColumns`
 * drives the admin table. `publicSort` is how the public site orders records.
 *
 * 所有可编辑集合。fields 同时驱动后台表单和服务端校验，listColumns 驱动后台列表，
 * publicSort 决定公开页排序。
 */
export const COLLECTIONS = {
  news: {
    labelZh: '新闻动态',
    labelEn: 'News',
    icon: '📰',
    route: '/news',
    hasBlocks: true,
    hasStatus: true,
    publicSort: { key: 'date', dir: 'desc' },
    listColumns: ['title', 'date', 'status'],
    fields: [
      { key: 'title', type: 'text', i18n: true, required: true, labelZh: '标题', labelEn: 'Title' },
      { key: 'date', type: 'date', required: true, labelZh: '日期', labelEn: 'Date' },
      { key: 'summary', type: 'textarea', i18n: true, labelZh: '摘要', labelEn: 'Summary' },
      { key: 'cover', type: 'upload', accept: 'image', labelZh: '封面图', labelEn: 'Cover image' },
      { key: 'tags', type: 'tags', labelZh: '标签', labelEn: 'Tags' },
      { key: 'pinned', type: 'boolean', labelZh: '置顶', labelEn: 'Pinned', default: false },
    ],
  },

  members: {
    labelZh: '研究成员',
    labelEn: 'Members',
    icon: '👥',
    route: '/members',
    hasBlocks: true,
    hasStatus: true,
    publicSort: { key: 'order', dir: 'asc' },
    listColumns: ['name', 'kind', 'role', 'status'],
    fields: [
      {
        key: 'kind',
        type: 'select',
        required: true,
        labelZh: '类别',
        labelEn: 'Category',
        default: 'student',
        options: [
          { value: 'faculty', labelZh: '教师', labelEn: 'Faculty' },
          { value: 'student', labelZh: '学生', labelEn: 'Student' },
        ],
      },
      { key: 'name', type: 'text', i18n: true, required: true, labelZh: '姓名', labelEn: 'Name' },
      {
        key: 'role',
        type: 'text',
        i18n: true,
        labelZh: '职称 / 年级',
        labelEn: 'Title / Year',
        hintZh: '教师填职称（副教授、讲师…），学生填年级（2024 级硕士…）',
        hintEn: 'Faculty: academic title. Student: cohort, e.g. "MSc, 2024".',
      },
      {
        key: 'degree',
        type: 'select',
        labelZh: '学位阶段',
        labelEn: 'Degree track',
        hintZh: '仅学生需要填',
        hintEn: 'Students only',
        options: [
          { value: '', labelZh: '（不适用）', labelEn: '(n/a)' },
          { value: 'phd', labelZh: '博士', labelEn: 'PhD' },
          { value: 'master', labelZh: '硕士', labelEn: 'Master' },
          { value: 'undergrad', labelZh: '本科', labelEn: 'Undergraduate' },
        ],
      },
      { key: 'avatar', type: 'upload', accept: 'image', labelZh: '照片', labelEn: 'Photo' },
      { key: 'email', type: 'text', labelZh: '邮箱', labelEn: 'Email' },
      { key: 'homepage', type: 'text', labelZh: '个人主页', labelEn: 'Homepage' },
      { key: 'github', type: 'text', labelZh: 'GitHub', labelEn: 'GitHub' },
      { key: 'scholar', type: 'text', labelZh: 'Google Scholar', labelEn: 'Google Scholar' },
      { key: 'interests', type: 'tags', i18n: true, labelZh: '研究兴趣', labelEn: 'Interests' },
      { key: 'bio', type: 'textarea', i18n: true, labelZh: '一句话简介', labelEn: 'Short bio' },
      {
        key: 'memberStatus',
        type: 'select',
        labelZh: '在组状态',
        labelEn: 'Standing',
        default: 'current',
        options: [
          { value: 'current', labelZh: '在组', labelEn: 'Current' },
          { value: 'alumni', labelZh: '已毕业 / 已离组', labelEn: 'Alumni' },
        ],
      },
      { key: 'graduationYear', type: 'text', labelZh: '毕业年份', labelEn: 'Graduation year' },
      { key: 'destination', type: 'text', i18n: true, labelZh: '毕业去向', labelEn: 'Destination' },
      { key: 'order', type: 'number', labelZh: '排序', labelEn: 'Sort order', default: 100 },
    ],
  },

  research: {
    labelZh: '研究项目',
    labelEn: 'Research Projects',
    icon: '🔬',
    route: '/research',
    hasBlocks: true,
    hasStatus: true,
    publicSort: { key: 'order', dir: 'asc' },
    listColumns: ['title', 'pi', 'funder', 'status'],
    fields: [
      { key: 'title', type: 'text', i18n: true, required: true, labelZh: '项目 / 方向名称', labelEn: 'Project / topic' },
      { key: 'summary', type: 'textarea', i18n: true, labelZh: '一段话概述', labelEn: 'Summary' },
      { key: 'cover', type: 'upload', accept: 'image', labelZh: '配图', labelEn: 'Figure' },

      /**
       * Who leads the project.
       *
       * Distinct from `role`, which says what the GROUP does on it — lead or
       * take part. With several faculty in one group those are different
       * questions, and answering only the second leaves every project reading as
       * though the same person ran it.
       *
       * Free text rather than a reference to a member record, because a project
       * may be led by someone who is not on this site: a collaborator elsewhere,
       * a colleague who keeps no page here. A picker would force such a project
       * to either invent a member or leave the field blank.
       *
       * 项目主持人。
       *
       * 与 role 不同：role 说的是「课题组」在项目里做什么——主持还是参与。
       * 一个组里有多位老师时，这是两个不同的问题；只回答后者，会让所有项目读起来
       * 都像是同一个人在做。
       *
       * 用自由文本而不是关联成员记录，因为主持人可能并不在本站上：外单位的合作者、
       * 不维护个人页的同事。做成选择器，这类项目就只能要么凭空造一个成员，
       * 要么把这一栏空着。
       */
      {
        key: 'pi',
        type: 'text',
        i18n: true,
        labelZh: '项目主持人',
        labelEn: 'Principal investigator',
        hintZh: '这个项目由哪位老师主持。可以是组外的人。',
        hintEn: 'Which member of staff leads it. May be someone outside the group.',
      },

      { key: 'funder', type: 'text', i18n: true, labelZh: '资助来源', labelEn: 'Funder' },
      { key: 'grantNo', type: 'text', labelZh: '项目编号', labelEn: 'Grant number' },
      {
        key: 'role',
        type: 'select',
        labelZh: '承担角色',
        labelEn: 'Role',
        default: 'pi',
        options: [
          { value: 'pi', labelZh: '主持', labelEn: 'PI' },
          { value: 'copi', labelZh: '参与', labelEn: 'Co-I' },
        ],
      },
      { key: 'years', type: 'text', labelZh: '起止时间', labelEn: 'Period' },
      { key: 'amount', type: 'text', labelZh: '经费', labelEn: 'Funding' },
      {
        key: 'projectStatus',
        type: 'select',
        labelZh: '进展',
        labelEn: 'Progress',
        default: 'ongoing',
        options: [
          { value: 'ongoing', labelZh: '在研', labelEn: 'Ongoing' },
          { value: 'finished', labelZh: '已结题', labelEn: 'Completed' },
        ],
      },
      { key: 'keywords', type: 'tags', i18n: true, labelZh: '关键词', labelEn: 'Keywords' },
      { key: 'order', type: 'number', labelZh: '排序', labelEn: 'Sort order', default: 100 },
    ],
  },

  /**
   * Demos: what the group's work actually produces when it runs.
   *
   * A separate collection from `research` rather than a second body on the same
   * record, because the two are not two halves of one thing. A project is
   * defined by a grant — funder, number, period, money — and exists whether or
   * not anything has been built yet. A demo is defined by an artefact — a
   * repository, a recording, a screenshot — and often spans several projects or
   * none. Giving each its own record means neither carries the other's empty
   * fields, and either list can grow without disturbing the other.
   *
   * 演示样例：课题组的工作跑起来之后长什么样。
   *
   * 与 research 分成两个集合，而不是同一条记录的第二段正文，因为二者并不是一件事的
   * 两半。项目由立项定义——资助方、编号、周期、经费——不管有没有做出东西，它都成立。
   * 演示样例由产物定义——一个仓库、一段录屏、一张截图——而且常常横跨多个项目，
   * 或者不属于任何项目。各自独立成记录，谁都不必背着对方用不上的空字段，
   * 两个列表也可以各自增长、互不牵动。
   */
  demos: {
    labelZh: '演示样例',
    labelEn: 'Demos',
    icon: '🎬',
    route: '/research/demos',
    hasBlocks: true,
    hasStatus: true,
    publicSort: { key: 'order', dir: 'asc' },
    listColumns: ['title', 'kind', 'year', 'status'],
    fields: [
      { key: 'title', type: 'text', i18n: true, required: true, labelZh: '名称', labelEn: 'Title' },
      {
        key: 'summary',
        type: 'textarea',
        i18n: true,
        labelZh: '一段话简介',
        labelEn: 'Summary',
        hintZh: '这个 demo 做什么、输入是什么、看点在哪。列表卡片上显示的就是它。',
        hintEn: 'What it does, what goes in, what to look at. This is what the card shows.',
      },
      {
        key: 'kind',
        type: 'select',
        labelZh: '类别',
        labelEn: 'Category',
        default: 'result',
        options: [
          { value: 'result', labelZh: '研究成果', labelEn: 'Research result' },
          { value: 'engineering', labelZh: '项目工程', labelEn: 'Engineering' },
        ],
      },
      {
        key: 'cover',
        type: 'upload',
        accept: 'image',
        labelZh: '封面图',
        labelEn: 'Cover',
        hintZh: '列表卡片上的图。截图、结果图、合同首页都可以。',
        hintEn: 'The card image — a screenshot, a result figure, a contract page.',
      },
      { key: 'year', type: 'text', labelZh: '时间', labelEn: 'Year' },
      { key: 'codeUrl', type: 'text', labelZh: '代码链接', labelEn: 'Code URL' },
      {
        key: 'videoUrl',
        type: 'text',
        labelZh: '视频链接',
        labelEn: 'Video URL',
        hintZh: 'B 站 / YouTube。视频文件也可以在正文里直接上传。',
        hintEn: 'Bilibili / YouTube. A video file can also be uploaded in the body.',
      },
      { key: 'keywords', type: 'tags', i18n: true, labelZh: '关键词', labelEn: 'Keywords' },
      {
        key: 'relatedProject',
        type: 'text',
        i18n: true,
        labelZh: '相关项目',
        labelEn: 'Related project',
        hintZh: '选填。属于哪个科研项目。',
        hintEn: 'Optional. Which project it belongs to.',
      },
      { key: 'order', type: 'number', labelZh: '排序', labelEn: 'Sort order', default: 100 },
    ],
  },

  publications: {
    labelZh: '发表论文',
    labelEn: 'Publications',
    icon: '📄',
    route: '/publications',
    hasBlocks: false,
    hasStatus: true,
    publicSort: { key: 'year', dir: 'desc' },
    listColumns: ['title', 'venueShort', 'year', 'status'],
    fields: [
      { key: 'title', type: 'text', required: true, labelZh: '标题', labelEn: 'Title' },
      {
        key: 'authors',
        type: 'text',
        required: true,
        labelZh: '作者',
        labelEn: 'Authors',
        hintZh: '逗号分隔，按署名顺序。组内成员姓名会自动加粗。',
        hintEn: 'Comma separated, in byline order. Group members are bolded automatically.',
      },
      { key: 'venue', type: 'text', labelZh: '发表载体全称', labelEn: 'Venue (full)' },
      { key: 'venueShort', type: 'text', labelZh: '简称', labelEn: 'Venue (short)', hintZh: '如 CVPR、TGRS', hintEn: 'e.g. CVPR, TGRS' },
      { key: 'year', type: 'number', required: true, labelZh: '年份', labelEn: 'Year' },
      {
        key: 'type',
        type: 'select',
        labelZh: '类型',
        labelEn: 'Type',
        default: 'journal',
        options: [
          { value: 'journal', labelZh: '期刊', labelEn: 'Journal' },
          { value: 'conference', labelZh: '会议', labelEn: 'Conference' },
          { value: 'preprint', labelZh: '预印本', labelEn: 'Preprint' },
        ],
      },
      { key: 'doi', type: 'text', labelZh: 'DOI', labelEn: 'DOI' },
      { key: 'url', type: 'text', labelZh: '论文链接', labelEn: 'Paper URL' },
      { key: 'pdf', type: 'upload', accept: 'doc', labelZh: '论文 PDF', labelEn: 'Paper PDF' },
      { key: 'thumbnail', type: 'upload', accept: 'image', labelZh: '缩略图', labelEn: 'Thumbnail', hintZh: '上传 PDF 后会自动生成首页缩略图', hintEn: 'Auto-generated from the PDF first page' },
      { key: 'codeUrl', type: 'text', labelZh: '代码链接', labelEn: 'Code URL' },
      { key: 'bibtex', type: 'textarea', labelZh: 'BibTeX', labelEn: 'BibTeX' },
      { key: 'citations', type: 'number', labelZh: '被引次数', labelEn: 'Citations', default: 0 },
      { key: 'highlight', type: 'text', i18n: true, labelZh: '亮点标记', labelEn: 'Badge', hintZh: '如「ESI 高被引」「Oral」', hintEn: 'e.g. "ESI Highly Cited", "Oral"' },
      { key: 'featured', type: 'boolean', labelZh: '首页展示', labelEn: 'Show on home', default: false },
    ],
  },

  patents: {
    labelZh: '专利软著',
    labelEn: 'Patents & Software',
    icon: '📜',
    route: '/achievements',
    hasBlocks: false,
    hasStatus: true,
    publicSort: { key: 'date', dir: 'desc' },
    listColumns: ['title', 'kind', 'date', 'status'],
    fields: [
      { key: 'title', type: 'text', i18n: true, required: true, labelZh: '名称', labelEn: 'Title' },
      {
        key: 'kind',
        type: 'select',
        required: true,
        labelZh: '类型',
        labelEn: 'Type',
        default: 'invention',
        options: [
          { value: 'invention', labelZh: '发明专利', labelEn: 'Invention patent' },
          { value: 'utility', labelZh: '实用新型', labelEn: 'Utility model' },
          { value: 'software', labelZh: '软件著作权', labelEn: 'Software copyright' },
        ],
      },
      { key: 'number', type: 'text', labelZh: '专利号 / 登记号', labelEn: 'Registration number' },
      { key: 'date', type: 'date', labelZh: '授权 / 登记日期', labelEn: 'Granted on' },
      { key: 'inventors', type: 'text', labelZh: '发明人 / 著作权人', labelEn: 'Inventors' },
      {
        key: 'patentStatus',
        type: 'select',
        labelZh: '状态',
        labelEn: 'Status',
        default: 'granted',
        options: [
          { value: 'granted', labelZh: '已授权', labelEn: 'Granted' },
          { value: 'pending', labelZh: '实审 / 公开', labelEn: 'Pending' },
        ],
      },
      {
        key: 'certificates',
        type: 'certificateList',
        labelZh: '证书文件',
        labelEn: 'Certificates',
        hintZh: '可传 PDF 或图片，站点统一显示为 PNG',
        hintEn: 'Upload PDF or image — the site always displays PNG',
      },
    ],
  },

  awards: {
    labelZh: '竞赛获奖',
    labelEn: 'Awards',
    icon: '🏆',
    route: '/achievements',
    hasBlocks: false,
    hasStatus: true,
    publicSort: { key: 'date', dir: 'desc' },
    listColumns: ['title', 'level', 'rank', 'status'],
    fields: [
      { key: 'title', type: 'text', i18n: true, required: true, labelZh: '获奖名称', labelEn: 'Award' },
      { key: 'competition', type: 'text', i18n: true, labelZh: '赛事名称', labelEn: 'Competition' },
      {
        key: 'level',
        type: 'select',
        labelZh: '级别',
        labelEn: 'Level',
        default: 'national',
        options: [
          { value: 'international', labelZh: '国际级', labelEn: 'International' },
          { value: 'national', labelZh: '国家级', labelEn: 'National' },
          { value: 'provincial', labelZh: '省部级', labelEn: 'Provincial' },
          { value: 'school', labelZh: '校级', labelEn: 'University' },
        ],
      },
      { key: 'rank', type: 'text', i18n: true, labelZh: '奖项等级', labelEn: 'Prize', hintZh: '如「一等奖」', hintEn: 'e.g. "First Prize"' },
      { key: 'date', type: 'date', labelZh: '获奖日期', labelEn: 'Date' },
      { key: 'recipients', type: 'text', labelZh: '获奖人', labelEn: 'Recipients' },
      { key: 'advisor', type: 'text', labelZh: '指导教师', labelEn: 'Advisor' },
      {
        key: 'certificates',
        type: 'certificateList',
        labelZh: '获奖证书 / 截图',
        labelEn: 'Certificates',
        hintZh: '可传 PDF 或图片，站点统一显示为 PNG',
        hintEn: 'Upload PDF or image — the site always displays PNG',
      },
    ],
  },
}

/** Collection keys in the order the admin sidebar shows them. 后台侧栏顺序。 */
export const COLLECTION_ORDER = [
  'news',
  'members',
  'research',
  'demos',
  'publications',
  'patents',
  'awards',
]

/**
 * Fields on the global site-settings blob.
 *
 * Admin-only. Everything the public shell needs that is not a collection.
 *
 * 全局站点设置。仅管理员可改。公开页外壳需要但不属于任何集合的内容都在这里。
 */
export const SITE_FIELDS = [
  { key: 'groupName', type: 'text', i18n: true, labelZh: '课题组名称', labelEn: 'Group name' },
  { key: 'groupShort', type: 'text', labelZh: '缩写', labelEn: 'Short name' },
  { key: 'tagline', type: 'text', i18n: true, labelZh: '一句话定位', labelEn: 'Tagline' },
  { key: 'intro', type: 'textarea', i18n: true, labelZh: '首页简介', labelEn: 'Home intro' },
  { key: 'institution', type: 'text', i18n: true, labelZh: '所属单位', labelEn: 'Institution' },
  { key: 'department', type: 'text', i18n: true, labelZh: '院系 / 实验室', labelEn: 'Department / Lab' },

  /**
   * Whether the institution and department are shown publicly.
   *
   * Naming a university on a group site can read as speaking for it, which a
   * group may not want and an institution may not welcome. The values stay in
   * the settings either way — this only controls whether visitors see them — so
   * turning the display off is not the same as deleting the affiliation and is
   * reversible in one click.
   *
   * 是否对外显示所属单位与院系。
   *
   * 在课题组主页上写出学校名字，容易被读成「代表学校发言」——这既未必是课题组的本意，
   * 学校也未必乐见。无论开关如何，值本身都留在设置里，这里只控制访客是否看得到；
   * 因此「关掉显示」不等于「删掉单位」，而且一键即可恢复。
   */
  {
    key: 'showAffiliation',
    type: 'boolean',
    default: true,
    labelZh: '公开显示所属单位',
    labelEn: 'Show affiliation publicly',
    hintZh: '关掉后，页脚与联系页不再显示单位和院系，设置里的内容仍然保留。',
    hintEn: 'When off, the footer and contact page stop showing them. The values are kept.',
  },
  { key: 'address', type: 'textarea', i18n: true, labelZh: '地址', labelEn: 'Address' },
  {
    key: 'email',
    type: 'text',
    labelZh: '联系邮箱',
    labelEn: 'Contact email',
    hintZh: '写成「地址（姓名）」也可以，链接会自动只取地址。想写清谁负责什么，用下面的「联系人」。',
    hintEn: 'You may write "address (Name)" — the link uses just the address. To say who handles what, use Contact people below.',
  },
  { key: 'phone', type: 'text', labelZh: '电话', labelEn: 'Phone' },

  /**
   * Named people to contact, rather than only an address and a mailbox.
   *
   * A general inbox tells a prospective student where to send mail but not who
   * reads it, and "someone will get back to you" is exactly the impression a
   * small group does not want to give. Each entry names a person and what to
   * write to them about, so a visitor can pick rather than guess.
   *
   * Repeatable because the right answer differs by enquiry — admissions and
   * industry collaboration are rarely the same person — and a fixed pair of
   * fields would force the group to pretend otherwise.
   *
   * 具名的联系人，而不只是一个地址和一个信箱。
   *
   * 公共信箱只告诉有意报考的学生「往哪寄」，不告诉他「谁会看」，而「会有人回复你」
   * 恰恰是小课题组最不该给人的印象。每一条都写明是谁、以及什么事该找他，
   * 访客可以自己挑，而不必猜。
   *
   * 做成可重复的，是因为不同事项的答案不同——招生咨询和产业合作很少是同一个人——
   * 固定两个字段等于逼课题组假装它们是。
   */
  {
    key: 'contactPeople',
    type: 'personList',
    labelZh: '联系人',
    labelEn: 'Contact people',
    hintZh: '写清是谁、负责什么事。留空则联系页只显示地址和公共邮箱。',
    hintEn: 'Say who they are and what to write to them about. Leave empty to show only the address and general mailbox.',
  },
  { key: 'github', type: 'text', labelZh: 'GitHub', labelEn: 'GitHub' },
  { key: 'scholar', type: 'text', labelZh: 'Google Scholar', labelEn: 'Google Scholar' },
  { key: 'wechatQr', type: 'upload', accept: 'image', labelZh: '微信二维码', labelEn: 'WeChat QR' },
  {
    key: 'heroImage',
    type: 'upload',
    accept: 'image',
    labelZh: '首页主图（浅色主题）',
    labelEn: 'Hero image (light theme)',
    hintZh: '深色主题若未单独设置，也用这一张。',
    hintEn: 'Also used for the dark theme when no dark version is set.',
  },

  /**
   * The dark theme's copy of the hero, because one picture cannot serve both.
   *
   * Luminance is zero-sum across the two backgrounds: a colour bright enough to separate
   * from near-black is, by that brightness, close to white. A single file has to optimise
   * whichever side is worse, and on the artwork this replaced that side measured a
   * contrast of 1.05 — 1.00 being indistinguishable from the background. The scan rings
   * were, quite literally, not there on the dark theme, and looked fine to whoever
   * reviewed the picture on a light one.
   *
   * Optional. Left empty, the light image is used for both, which is right for a photo or
   * a logo — anything whose subject carries its own contrast. It is the flat, mostly
   * transparent artwork that needs two versions.
   *
   * 深色主题用的那一张主图，因为一张图服务不了两种背景。
   *
   * 亮度在两种背景之间是零和的：一个亮到足以从近黑中分离出来的颜色，也正因为这份亮，
   * 而离白色很近。单张文件只能去优化较差的那一侧，而在被它替换掉的那张图上，
   * 那一侧实测对比度是 1.05——1.00 即「与背景无法区分」。扫描环在深色主题下字面意义上
   * 不存在，而在浅色主题下审阅它的人只会觉得一切正常。
   *
   * 可留空。留空则两种主题都用上面那张，这对照片或标志是对的——凡是主体自带对比度的
   * 都不需要两版。真正需要两版的，是这种平涂的、大部分透明的图。
   */
  {
    key: 'heroImageDark',
    type: 'upload',
    accept: 'image',
    labelZh: '首页主图（深色主题）',
    labelEn: 'Hero image (dark theme)',
    hintZh: '留空则深色主题沿用上面那张。平涂的透明底插图通常需要单独一版。',
    hintEn: 'Leave empty to reuse the one above. Flat artwork on a transparent background usually needs its own version.',
  },

  /**
   * How the hero image sits against the type on a wide screen.
   *
   * A setting rather than a decision, because it is not one anybody can make
   * once. Which arrangement looks right depends on the picture currently in
   * `heroImage` — a wide strip and a tall square want opposite things — and the
   * picture is two clicks away from changing. Hard-coding the arrangement would
   * mean every future image inherits a layout chosen for an image it has never
   * seen.
   *
   * It sits next to `heroImage` rather than on the appearance page with the type
   * scale. The type scale is global and its own decision; this is a property of
   * the picture directly above it, and splitting one choice across two console
   * pages is friction for no gain.
   *
   * Narrow screens ignore it: below ~900px both arrangements collapse to the
   * image beneath the type, because there is no room beside it and the picture
   * has to say its piece across the full width.
   *
   * 宽屏下主图与文字的相对位置。
   *
   * 做成设置而不是做成决定，因为这件事没人能一次性定下来。哪种排布好看，取决于此刻
   * `heroImage` 里是哪张图——宽长条和近方形要的正好相反——而换图只是两下点击的事。
   * 把排布写死，等于让将来每一张图都继承一个「为它从未见过的另一张图」所选的版式。
   *
   * 它放在 `heroImage` 旁边，而不是和全站字号一起放到外观页。字号是全局的、是它自己的
   * 决定；而这一项是紧挨其上那张图的属性，把一个选择拆到两个后台页面上，只有摩擦、
   * 没有好处。
   *
   * 窄屏忽略此项：约 900px 以下两种排布都退化为「图在文字下方」——旁边没有位置，
   * 而图需要整幅宽度才能把话说完。
   */
  {
    key: 'heroLayout',
    type: 'select',
    labelZh: '首页主图版式',
    labelEn: 'Hero layout',
    default: 'side',
    hintZh: '右侧：图在标题右侧、略微倾斜，与文字相接处淡出。整栏：图铺满整条区域，文字压在图上。手机上两者相同。',
    hintEn: 'Beside: the picture sits right of the title, slightly tilted, fading where it meets the words. Band: it fills the whole strip with the text over it. Identical on a phone.',
    options: [
      { value: 'side', labelZh: '右侧区域', labelEn: 'Beside the type' },
      { value: 'band', labelZh: '整栏', labelEn: 'Full band' },
    ],
  },
  { key: 'joinNote', type: 'textarea', i18n: true, labelZh: '招生 / 加入我们', labelEn: 'Join us' },
  { key: 'icp', type: 'text', labelZh: '备案号', labelEn: 'ICP filing' },

  /**
   * Site-wide type scale.
   *
   * The only appearance control the console exposes, and it is admin-only
   * because site settings are. It multiplies the theme's whole type scale rather
   * than setting any particular size: a per-heading font-size control is how a
   * site ends up with nine different heading sizes, which is exactly what the
   * block editor exists to prevent. One number moves everything together and the
   * proportions the theme chose survive.
   *
   * 全站字号档位。
   *
   * 控制台暴露的唯一外观开关，仅管理员可用——因为站点设置本身就仅管理员可用。
   * 它整体缩放主题的字阶，而不是设定某一处的具体字号：「逐个标题设字号」正是一个站点
   * 最终出现九种标题大小的原因，而这恰恰是块编辑器要防的事。一个数字带动全部，
   * 主题定下的比例关系得以保留。
   */
  {
    key: 'typeScale',
    // Grouped out of the main settings form and onto its own console page.
    // It is the one setting that changes how the site LOOKS rather than what it
    // says, and mixing it in among addresses and phone numbers invites someone
    // to change it while looking for something else.
    // 从主设置表单里分出来，放到控制台自己的页面上。它是唯一一个改变站点「长相」
    // 而非「内容」的设置；把它混在地址和电话中间，等于邀请人在找别的东西时顺手改掉它。
    group: 'appearance',
    type: 'select',
    labelZh: '全站字号',
    labelEn: 'Type scale',
    default: '1',
    hintZh: '整体缩放所有文字。改完刷新公开页即可看到。',
    hintEn: 'Scales all text together. Reload a public page to see it.',
    options: [
      { value: '1', labelZh: '标准', labelEn: 'Standard' },
      { value: '1.06', labelZh: '稍大', labelEn: 'Large' },
      { value: '1.12', labelZh: '更大', labelEn: 'Larger' },
    ],
  },
]

/**
 * Build the site-settings object with every declared field present.
 *
 * The server has the same job at seed time; both call this so a field's default
 * and its empty shape are declared once. A repeatable field must start as an
 * array and a boolean as its declared default — not as `''` — or the admin form
 * renders it wrongly the first time it is opened.
 *
 * 构造站点设置对象，声明过的字段都存在。
 *
 * 服务端在写种子数据时做同样的事；两边都调用它，因此字段的默认值和空值形状只声明
 * 一次。可重复字段必须以数组开始、布尔字段以其声明的默认值开始——而不是 ''——
 * 否则后台表单第一次打开时就会渲染错。
 *
 * @returns {object} Blank settings / 空白设置。
 */
export function blankSiteSettings() {
  const site = {}
  for (const field of SITE_FIELDS) {
    if (field.default !== undefined) site[field.key] = field.default
    else if (field.type === 'personList') site[field.key] = []
    else if (field.type === 'boolean') site[field.key] = false
    else site[field.key] = field.i18n ? { zh: '', en: '' } : ''
  }
  return site
}

/**
 * Site fields belonging to one group.
 *
 * Settings split by what they change, not by what they are: everything that
 * alters the site's *content* is one page, and the single setting that alters
 * how it *looks* gets its own. Mixing an appearance control in among addresses
 * and phone numbers invites someone to change it while looking for something
 * else. Fields with no declared group default to 'general'.
 *
 * 按分组取站点设置字段。
 *
 * 设置按「改变什么」而非「是什么」拆分：所有改变站点**内容**的归一页，
 * 唯一改变站点**外观**的那一项独占一页。把外观开关混在地址和电话中间，
 * 等于邀请人在找别的东西时顺手改掉它。没有声明分组的字段默认属于 general。
 *
 * @param {string} group Group name / 分组名。
 * @returns {Array<object>} Matching fields / 匹配的字段。
 */
export function siteFieldsIn(group) {
  return SITE_FIELDS.filter((field) => (field.group || 'general') === group)
}

/**
 * The block documents a collection carries.
 *
 * Most collections have exactly one, stored under `blocks`. A collection may
 * instead declare `blockSections` to carry several — `research` splits its page
 * into a project write-up and a demo. Callers should always go through this
 * function rather than reading `record.blocks` directly, so adding a second
 * document to another collection later needs no changes at the call sites.
 *
 * 一个集合携带的块文档。
 *
 * 多数集合只有一份，存在 `blocks` 下。集合也可以声明 `blockSections` 来携带多份——
 * research 就把页面拆成「项目说明」和「Demo」两份。调用方一律走这个函数，
 * 不要直接读 `record.blocks`，这样将来给别的集合加第二份文档时，调用处无需改动。
 *
 * @param {string} collectionKey Key in COLLECTIONS / 集合键名。
 * @returns {Array<{key: string, labelZh: string, labelEn: string, templateGroup: ?string}>} Sections / 分段定义。
 */
export function blockSectionsOf(collectionKey) {
  const collection = COLLECTIONS[collectionKey]
  if (!collection?.hasBlocks) return []
  if (collection.blockSections?.length) return collection.blockSections
  return [{ key: 'blocks', labelZh: '正文', labelEn: 'Body', templateGroup: null }]
}

/**
 * Build an empty record for a collection using each field's declared default.
 *
 * 按字段声明的默认值构造一条空记录。
 *
 * @param {string} collectionKey Key in COLLECTIONS / 集合键名。
 * @returns {object} A blank record / 一条空白记录。
 */
export function blankRecord(collectionKey) {
  const collection = COLLECTIONS[collectionKey]
  if (!collection) throw new Error(`Unknown collection: ${collectionKey}`)
  const record = {}
  for (const field of collection.fields) {
    if (field.default !== undefined) {
      record[field.key] = field.default
    } else if (field.i18n) {
      record[field.key] = field.type === 'tags' ? { zh: [], en: [] } : emptyI18n()
    } else if (field.type === 'tags') {
      record[field.key] = []
    } else if (field.type === 'certificateList') {
      record[field.key] = []
    } else if (field.type === 'number') {
      record[field.key] = 0
    } else if (field.type === 'boolean') {
      record[field.key] = false
    } else {
      record[field.key] = ''
    }
  }
  // One empty array per declared block document, not just `blocks`.
  // 每个声明的块文档各给一个空数组，而不只是 blocks。
  for (const section of blockSectionsOf(collectionKey)) record[section.key] = []
  if (collection.hasStatus) record.status = 'draft'
  return record
}

/**
 * Validate a record against its collection definition.
 *
 * Server-side gate; the admin form uses the same rules for inline feedback so
 * the two never disagree.
 *
 * 按集合定义校验一条记录。服务端做拦截，后台表单用同一套规则做即时提示，
 * 两边不会打架。
 *
 * @param {string} collectionKey Key in COLLECTIONS / 集合键名。
 * @param {object} record Record to check / 待校验记录。
 * @returns {string[]} Human-readable errors, empty when valid / 错误信息，合法则为空。
 */
export function validateRecord(collectionKey, record) {
  const collection = COLLECTIONS[collectionKey]
  if (!collection) return [`Unknown collection: ${collectionKey}`]
  const errors = []
  for (const field of collection.fields) {
    if (!field.required) continue
    const value = record[field.key]
    const missing = field.i18n
      ? !value || (!String(value.zh || '').trim() && !String(value.en || '').trim())
      : value === undefined || value === null || String(value).trim() === ''
    if (missing) errors.push(`${field.labelZh} / ${field.labelEn} is required`)
  }
  return errors
}
